import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTableSession } from "../../context/TableSessionContext";
import { enterTableSessionMenu } from "../../utils/tableSessionEntry";

// Route: /table/join/:token - matches exactly the join_url the backend builds
// (smartpay-dashboard controller/tableSessionInvites.js#buildJoinUrl: `${baseUrl}/table/join/${token}`).
// Deliberately NOT /table-session/join/:inviteToken and NOT /table/:publicTableCode - those are
// different things (the latter is the physical-QR entry point, TableEntryPage.jsx).

const STATE = {
  LOADING: "loading",
  INVALID: "invalid",
  EXPIRED: "expired",
  CONSUMED: "consumed",
  SESSION_CLOSED: "session_closed",
  ERROR: "error",
  READY: "ready",
  JOINING: "joining",
};

// Maps the codes getInvitePublicInfo() can return (smartpay-dashboard services/tableSessionInvites.js)
// to one of our states.
const INVITE_CODE_STATE = {
  INVITE_NOT_FOUND: STATE.INVALID,
  INVITE_REVOKED: STATE.INVALID,
  INVITE_EXPIRED: STATE.EXPIRED,
  INVITE_ALREADY_CONSUMED: STATE.CONSUMED,
  SESSION_NOT_ACTIVE: STATE.SESSION_CLOSED,
};

const STATE_TITLE = {
  [STATE.INVALID]: "Invitacion no valida",
  [STATE.EXPIRED]: "Invitacion expirada",
  [STATE.CONSUMED]: "Invitacion ya utilizada",
  [STATE.SESSION_CLOSED]: "Mesa cerrada",
  [STATE.ERROR]: "No fue posible validar la invitacion",
};

const STATE_MESSAGE = {
  [STATE.INVALID]: "Este enlace de invitacion no es valido. Pide a alguien de la mesa que genere uno nuevo.",
  [STATE.EXPIRED]: "Esta invitacion ya expiro. Pide a alguien de la mesa que genere una nueva.",
  [STATE.CONSUMED]: "Esta invitacion ya fue utilizada por otra persona. Pide una nueva.",
  [STATE.SESSION_CLOSED]: "La mesa ya no tiene una sesion activa.",
  [STATE.ERROR]: "Intenta abrir el enlace de nuevo.",
};

const formatCountdown = (expiresAt) => {
  if (!expiresAt) return "";
  const remainingMs = new Date(expiresAt).getTime() - Date.now();
  if (remainingMs <= 0) return "0:00";
  const totalSeconds = Math.floor(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const TableSessionJoinPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { validateSessionInvite, joinSessionWithInvite, refreshCurrentSession } = useTableSession();

  const [state, setState] = useState(STATE.LOADING);
  const [invitePreview, setInvitePreview] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [nameError, setNameError] = useState("");
  const [joinError, setJoinError] = useState("");
  const [countdownTick, setCountdownTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const validate = async () => {
      setState(STATE.LOADING);
      try {
        const info = await validateSessionInvite(token);
        if (cancelled) return;

        if (!info?.valid) {
          setState(INVITE_CODE_STATE[info?.code] || STATE.INVALID);
          return;
        }

        setInvitePreview(info);
        setState(STATE.READY);
      } catch (err) {
        if (!cancelled) setState(STATE.ERROR);
      }
    };

    validate();
    return () => { cancelled = true; };
  }, [token, validateSessionInvite]);

  // Live countdown while the invite is still valid - purely cosmetic (the backend is the real
  // source of truth on expiry), re-validating on its own would just be extra load for no benefit.
  useEffect(() => {
    if (state !== STATE.READY || !invitePreview?.expires_at) return undefined;
    const interval = setInterval(() => setCountdownTick((tick) => tick + 1), 1000);
    return () => clearInterval(interval);
  }, [state, invitePreview?.expires_at]);

  const countdownLabel = useMemo(() => (
    invitePreview?.expires_at ? formatCountdown(invitePreview.expires_at) : ""
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [invitePreview?.expires_at, countdownTick]);

  const onJoin = useCallback(async () => {
    const trimmed = displayName.trim();
    if (!trimmed) {
      setNameError("Ingresa tu nombre para unirte al pedido.");
      return;
    }
    setNameError("");
    setJoinError("");
    setState(STATE.JOINING);

    try {
      await joinSessionWithInvite(token, trimmed);
      const current = await refreshCurrentSession();
      if (!current) throw new Error("No fue posible recuperar la sesion despues de unirse.");
      // Same business/branchoffice/machine context setup as the physical-QR flow (TableEntryPage) -
      // this device needs it too before /menu can render anything useful.
      await enterTableSessionMenu(current);
      navigate("/menu", { replace: true });
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code && INVITE_CODE_STATE[code]) {
        setState(INVITE_CODE_STATE[code]);
        return;
      }
      setJoinError("No fue posible unirte al pedido. Intenta nuevamente.");
      setState(STATE.READY);
    }
  }, [displayName, token, joinSessionWithInvite, refreshCurrentSession, navigate]);

  const isTerminalState = [STATE.INVALID, STATE.EXPIRED, STATE.CONSUMED, STATE.SESSION_CLOSED, STATE.ERROR].includes(state);

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-md-8 col-lg-6">
          <div className="border rounded-3 p-4 bg-white shadow-sm">
            <p className="text-uppercase text-muted small mb-2">SmartPay mesa</p>
            <h1 className="h3 mb-3">Unirte al pedido </h1>

            {state === STATE.LOADING ? (
              <p className="mb-0">Validando invitacion...</p>
            ) : isTerminalState ? (
              <div className="alert alert-warning mb-0">
                <p className="mb-1 fw-bold">{STATE_TITLE[state]}</p>
                <p className="mb-0">{STATE_MESSAGE[state]}</p>
              </div>
            ) : (
              <>
                <dl className="row mb-3">
                  <dt className="col-4">Sucursal</dt>
                  <dd className="col-8">{invitePreview?.branchoffice?.name || "Sucursal"}</dd>
                  <dt className="col-4">Mesa</dt>
                  <dd className="col-8">{invitePreview?.table?.name || "Mesa"}</dd>
                </dl>

                {countdownLabel ? (
                  <p className="text-muted small mb-3">Expira en {countdownLabel}</p>
                ) : null}

                <div className="mb-3">
                  <label htmlFor="joinDisplayName" className="form-label">
                    Tu nombre
                  </label>
                  <input
                    id="joinDisplayName"
                    type="text"
                    className={`form-control${nameError ? " is-invalid" : ""}`}
                    placeholder="Escribe tu nombre"
                    value={displayName}
                    disabled={state === STATE.JOINING}
                    onChange={(e) => {
                      setDisplayName(e.target.value);
                      if (nameError) setNameError("");
                    }}
                  />
                  {nameError ? <div className="invalid-feedback">{nameError}</div> : null}
                </div>

                <button
                  type="button"
                  className="btn btn-dark w-100"
                  disabled={state === STATE.JOINING}
                  onClick={onJoin}
                >
                  {state === STATE.JOINING ? "Uniendote..." : "Unirme al pedido"}
                </button>

                {joinError ? <div className="alert alert-warning mt-3 mb-0">{joinError}</div> : null}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableSessionJoinPage;
